import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { citationIdFromHref, frameIdFromSrc, isAllowedImageSrc, safeUrl, withCitationLinks } from '../citations.ts';

interface Props {
  body: string;
  frames: Record<string, string>;
  /** Markers that exist for this SOP; unknown markers render as plain text instead of dead chips. */
  knownMarkers: Set<string>;
  onCite: (marker: string) => void;
}

const SopSectionBody: React.FC<Props> = ({ body, frames, knownMarkers, onCite }) => (
  <div className="sop-body max-w-none space-y-3 text-sm leading-6 text-gray-800 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-gray-200 [&_td]:px-3 [&_td]:py-1.5 [&_th]:border [&_th]:border-gray-200 [&_th]:bg-gray-50 [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-left [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      urlTransform={safeUrl}
      components={{
        a: ({ href, children }) => {
          const marker = citationIdFromHref(href);
          if (marker) {
            if (!knownMarkers.has(marker)) return <span>{children}</span>;
            return (
              <button
                type="button"
                onClick={() => onCite(marker)}
                className="mx-0.5 inline-flex items-center rounded bg-blue-50 px-1.5 text-xs font-medium text-blue-700 align-baseline hover:bg-blue-100"
                aria-label={`Show source for citation ${marker.slice(1)}`}
              >
                {marker.slice(1)}
              </button>
            );
          }
          return (
            <a href={href} target="_blank" rel="noreferrer" className="text-blue-700 underline">
              {children}
            </a>
          );
        },
        img: ({ src, alt }) => {
          if (!isAllowedImageSrc(src)) {
            return <span className="text-xs italic text-gray-400">[image removed]</span>;
          }
          const id = frameIdFromSrc(src);
          const url = id ? frames[id] : undefined;
          if (!url) return <span className="text-xs italic text-gray-400">[screenshot unavailable]</span>;
          return (
            <figure className="my-2">
              <img src={url} alt={alt ?? ''} loading="lazy" className="max-h-96 rounded border border-gray-200" />
              {alt && <figcaption className="mt-1 text-xs text-gray-500">{alt}</figcaption>}
            </figure>
          );
        },
      }}
    >
      {withCitationLinks(body)}
    </ReactMarkdown>
  </div>
);

export default SopSectionBody;
